import debug from "debug";

import {
  createEvoClient,
  getComplaints,
  getUsersPublicDomainConfiguration,
  updateConsents,
  updateCustomAttribute,
  updateCustomAttributes,
} from "@jti/evo-client";
import type { CustomAttributeValues } from "@jti/fe-common";
import type {
  AdditionalFieldResponseType,
  CasCustomAttrsApiContract,
  CustomAttributeWriteResponse,
} from "@jti/rest-api-types";

import { prepareEvoCustomAttributes } from "./utils/prepareCustomAttributes";

const log = debug("desjti:api:UserCustomAttrsApiEvoV5");

export class UserCustomAttrsApiEvo implements CasCustomAttrsApiContract {
  readonly name = "UserCustomAttrsApi";
  private readonly client;
  private readonly userUuid: string;
  constructor({
    baseUrl,
    tenantToken,
    ciamToken,
    recaptchaId = "",
    userUuid,
  }: {
    baseUrl: string;
    tenantToken: string;
    ciamToken: string;
    recaptchaId?: string;
    userUuid: string;
  }) {
    this.userUuid = userUuid;
    this.client = createEvoClient({
      baseUrl,
      recaptchaId,
      tenantToken,
      throwOnError: true,
      ciamToken,
    });
  }

  async getCustomAttributes(): Promise<AdditionalFieldResponseType> {
    const { data, response } = await getUsersPublicDomainConfiguration({
      client: this.client,
    });

    const customAttributes = data?.custom_attributes;

    log("Custom attributes from API", customAttributes);
    return {
      data: prepareEvoCustomAttributes(customAttributes),
      statusCode: response.status,
    };
  }

  async write(updatedCustomAttr: CustomAttributeValues): Promise<CustomAttributeWriteResponse> {
    log("Writing custom attributes", updatedCustomAttr);

    const { data } = await updateCustomAttributes({
      client: this.client,
      path: {
        uuid: this.userUuid,
      },
      body: updatedCustomAttr,
    });
    return {
      dob: data?.date_of_birth ?? "",
      email: data?.email ?? "",
      firstname: data?.first_name ?? "",
      lastname: data?.last_name ?? "",
    };
  }

  async writeConsents(
    updatedCustomAttr: Required<Pick<CustomAttributeValues, "consents" | "preferredLanguage">>
  ): Promise<void> {
    await updateConsents({
      client: this.client,
      path: {
        uuid: this.userUuid,
      },
      body: {
        consents:
          updatedCustomAttr.consents?.map((consent) => ({
            id: consent.id,
            accepted: consent.accepted ?? false,
            one_off: consent.one_off,
          })) ?? [],
      },
    });

    if (!updatedCustomAttr.preferredLanguage) {
      log("Skipping sending preferred language, because it was not provided");
      return;
    }

    await updateCustomAttribute({
      client: this.client,
      path: {
        uuid: this.userUuid,
        customAttribute: "preferredLanguage",
      },
      body: {
        value: updatedCustomAttr.preferredLanguage,
      },
      throwOnError: false,
    });
  }

  async getIsUserHasComplaints(): Promise<boolean> {
    const result = await getComplaints({
      client: this.client,
      path: {
        uuid: this.userUuid,
      },
    });

    // EVO schema types response as flat { has_complaints } but actual API wraps it in { data: { has_complaints } }
    const data = result.data as { has_complaints?: boolean; data?: { has_complaints?: boolean } };
    return data?.data?.has_complaints ?? data?.has_complaints ?? false;
  }
}
